import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash-es/get';
import isNil from 'lodash-es/isNil';
import isEmpty from 'lodash-es/isEmpty';

import { FieldsEnum, FormsEnum } from 'app/shared/enums';
import {
  getQuotationId,
  QuotationActions,
  selectQuotationCepikMergeResult,
} from 'app/store/data/quotation';
import { parseCepikMergeResultKey } from 'app/shared/helpers/parseCepikMergeResultKey';
import { QuotationService } from 'app/services/quotation.service';

const carDataFields = [
  FieldsEnum.CarProductionYear,
  FieldsEnum.CarMake,
  FieldsEnum.CarModel,
  FieldsEnum.CarFuelType,
  FieldsEnum.CarEngineCapacity,
  FieldsEnum.CarDoorsCount,
  FieldsEnum.CarVersionEt,
];

const relatedFields = {
  [FieldsEnum.OwnerDriverLicenseYear]: FieldsEnum.OwnerNoDrivingLicense,
  [FieldsEnum.OwnerNoDrivingLicense]: FieldsEnum.OwnerDriverLicenseYear,
  [FieldsEnum.Coowners1LicenseYear]: FieldsEnum.Coowners1NoDrivingLicense,
  [FieldsEnum.Coowners1NoDrivingLicense]: FieldsEnum.Coowners1LicenseYear,
  [FieldsEnum.Coowners2LicenseYear]: FieldsEnum.Coowners2NoDrivingLicense,
  [FieldsEnum.Coowners2NoDrivingLicense]: FieldsEnum.Coowners2LicenseYear,
  [FieldsEnum.CarRegistrationNumber]: FieldsEnum.CarRegistrationNumberNotKnown,
  [FieldsEnum.CarRegistrationNumberNotKnown]: FieldsEnum.CarRegistrationNumber,
};

export const fieldsWithoutConfirmationLabel = [
  FieldsEnum.OwnerNoDrivingLicense,
  FieldsEnum.Coowners1NoDrivingLicense,
  FieldsEnum.Coowners2NoDrivingLicense,
  FieldsEnum.CarRegistrationNumberNotKnown,
];

export function useCepikFormRowContainer(
  fieldKey?: FieldsEnum,
  formName?: FormsEnum
) {
  const dispatch = useDispatch();
  const cepikMergeResult = useSelector(selectQuotationCepikMergeResult);
  const quotationId = useSelector(getQuotationId);
  const hasAnyMergeResult = !!cepikMergeResult && !isEmpty(cepikMergeResult);

  const getFieldConfirmation = useCallback(
    (fieldKey?: FieldsEnum) => {
      const key = fieldKey?.endsWith('CheckoutForm')
        ? fieldKey.substring(0, fieldKey.length - 12)
        : fieldKey;

      if (!key) {
        const everyCarFieldConfirmed = carDataFields.every((field) => {
          const parsedFieldKey = parseCepikMergeResultKey(field);

          return get(cepikMergeResult, parsedFieldKey);
        });
        const everyCarFieldNeedConfirmation = carDataFields.every((field) => {
          const parsedFieldKey = parseCepikMergeResultKey(field);

          return get(cepikMergeResult, parsedFieldKey) === false;
        });

        return everyCarFieldConfirmed
          ? true
          : everyCarFieldNeedConfirmation
          ? false
          : null;
      } else {
        const parsedFieldKey = parseCepikMergeResultKey(key);
        const hasRelatedField = Object.keys(relatedFields).includes(key);

        if (hasRelatedField) {
          const relatedFieldKey = get(relatedFields, key, null);
          const parsedRelatedFieldKey = parseCepikMergeResultKey(
            relatedFieldKey
          );

          const fieldStatus = get(cepikMergeResult, parsedFieldKey, null);
          const relatedFieldStatus = get(
            cepikMergeResult,
            parsedRelatedFieldKey,
            null
          );

          if (isNil(fieldStatus) && isNil(relatedFieldStatus)) return null;
          if (fieldStatus || relatedFieldStatus)
            return fieldsWithoutConfirmationLabel.includes(key as FieldsEnum)
              ? null
              : true;
          return false;
        }

        return get(cepikMergeResult, parsedFieldKey, null);
      }
    },
    [cepikMergeResult]
  );

  const isFieldConfirmed = getFieldConfirmation(fieldKey);

  const isFieldConfirmationRequired =
    !isFieldConfirmed && !isNil(isFieldConfirmed);

  const onConfirmationChange = async (e: React.ChangeEvent) => {
    if (!fieldKey || !formName) return;

    const key = fieldKey?.endsWith('CheckoutForm')
      ? fieldKey.substring(0, fieldKey.length - 12)
      : fieldKey;

    const parsedFieldKey = parseCepikMergeResultKey(key);
    const newState = (e.target as HTMLInputElement).checked;

    dispatch(
      QuotationActions.setQuotationQuestion(
        `cepikMergeResult.${parsedFieldKey}`,
        newState
      )
    );

    QuotationService.update(quotationId, 'cepikMergeResult', {
      [parsedFieldKey]: newState,
    });

    const hasRelatedField = Object.keys(relatedFields).includes(key);

    if (hasRelatedField) {
      const relatedFieldKey = get(relatedFields, key, null);
      const parsedRelatedFieldKey = parseCepikMergeResultKey(relatedFieldKey);

      dispatch(
        QuotationActions.setQuotationQuestion(
          `cepikMergeResult.${parsedRelatedFieldKey}`,
          newState
        )
      );

      QuotationService.update(quotationId, 'cepikMergeResult', {
        [parsedRelatedFieldKey]: newState,
      });
    }
  };

  return {
    isFieldConfirmationRequired,
    isFieldConfirmed,
    hasAnyMergeResult,
    onConfirmationChange,
  };
}
